import React, { useEffect, useState, useRef } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { createSelector } from 'reselect';
import _ from 'lodash';
import cx from 'classnames';

import { isPropSet, didPropChange } from 'utils/props';
import { move } from 'utils/utils';
import { Urls } from 'constants/constants';

import AppState, { selectCurrentUser, selectCurrentUserId, selectConfigItem } from 'store/AppState';
import Overlay, { selectDefaultOverlay, selectOverlay, selectOverlays, selectSlideshow, diffSceneSettings } from 'store/models/Overlay';
import Channel, { selectChannel, selectChannels } from 'store/models/Channel';
import Campaign, { selectAllCampaigns } from 'store/models/Campaign';
import { selectCurrentAccount, getStreamerByChannelId } from 'store/models/Account';
import { selectData } from 'store/RouteState';


import Scene, {
  selectScenesByIds,
  MEDIA_UPLOAD_START,
  MEDIA_UPLOAD_SIG,
  MEDIA_UPLOAD,
  MEDIA_UPLOAD_COMPLETE
} from 'store/models/Scene';

import RouteState, { replaceTo } from 'store/RouteState';

import BodyTag from 'components/BodyTag';
import { Content } from 'components/Content';
import { SidePanel, SidePanelHeader, SidePanelTitle, SidePanelBody, SidePanelPopup } from 'components/SidePanel';
import ActionBar from 'components/ActionBar';
import SceneStrip from 'components/SceneStrip';
import Scrollbars from 'components/Scrollbars';
import Slideshow from 'components/Slideshow';
import FileDrop from 'components/FileDrop';
import OverlaySettings from 'components/OverlaySettings';
import SceneSettings from 'components/SceneSettings';
import SceneLibrary from 'components/SceneLibrary';
import { BackIconButton, PlayIconButton, SaveButton, PauseIconButton } from 'components/Button';
import Avatar from 'components/Avatar';
import SelectInput, { SelectItem } from 'components/forms/SelectInput';
import ChannelSelect from 'components/ChannelSelect';
import OverlaySelect from 'components/OverlaySelect';

import { SettingsIcon } from 'components/Svg';

const DEFAULT_SETTINGS = {
  length: 5000,
  background: null,
  backgroundOpacity: 100,
}


// ======================
// STORE
// ====================== 

const selectCurrentChannelId = (state, { channelId }) => channelId ? channelId : selectChannels(state) |> _.first |> #?._id;
const selectCurrentOverlay = (state, { overlayId }) => overlayId ? selectOverlay(state, overlayId) : selectDefaultOverlay(state);
const selectCurrentSlideShow = (state, props) => selectSlideshow(state, selectCurrentOverlay(state,props)?.code, getStreamerByChannelId(selectCurrentAccount(state), selectCurrentChannelId(state,props)));
const selector = createSelector(
  selectCurrentUser,
  selectCurrentAccount,
  selectCurrentOverlay,
  selectOverlays,
  selectCurrentSlideShow,
  (state,props) => selectScenesByIds(state, selectCurrentSlideShow(state,props)?.sceneIds),
  (state, props) => selectChannel(state, selectCurrentChannelId(state,props)),
  selectAllCampaigns,
  selectChannels,
  state => selectData(state, 'back'),
  (user, account, overlay, overlays, slideShow, scenes, channel, campaigns, channels, navBack) => {
    
    let { sceneSettings, overlaySettings, defaultScenes: defaultSceneIds, campaignsByScene, scenesByCampaign } = slideShow ?? {};
    return {
    user,
    account,
    overlay,
    overlays,
    scenes,
    channel,
    defaultSceneIds,
    campaignsByScene,
    scenesByCampaign,
    overlaySettings,
    sceneSettings,
    campaigns,
    channels,
    navBack
  }}
)


// ======================
// COMPONENT
// ====================== 
const SlideshowEditScreen = ({ match }) => {
  let { channelId, overlayId } = match?.params;
  let { user, account, overlay, overlays, channel, channels, scenes, campaigns, campaignsByScene, scenesByCampaign, overlaySettings, sceneSettings, navBack } = useSelector(state => selector(state, { channelId, overlayId }));
  const dispatch = useDispatch();
  

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [playingIndex, setPlayingIndex] = useState(0);
  const [breakIndex, setBreakIndex] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [editedOverlaySettings, setEditedOverlaySettings] = useState({});
  const [hasEditedOverlaySettings, setHasEditedOverlaySettings] = useState(false);
  const [editedSceneSettings, setEditedSceneSettings] = useState({});
  const [editedSceneIds, setEditedSceneIds] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [renderKey, setRenderKey] = useState(0);

  const slideshowRef = useRef(null);
  let selectedScene = scenes?.[selectedIndex];
  let showSlidePanel = !_.isNil(selectedScene);
  let hasMultipleOverlays = _.size(overlays) > 1;

  const fetchData = () => Promise.all([
    dispatch(Channel.actions.fetchChannels()),
    dispatch(Campaign.actions.fetchCampaigns()),
    dispatch(Overlay.actions.fetchOverlay()),
    dispatch(Overlay.actions.fetchOverlays()),
  ]);

  const fetchOverlayData = async () => {
    setLoading(true);
    await dispatch(Overlay.actions.getSlideshow(overlay._id, channel._id, { incl_meta: true }))
    setLoading(false);
    playSlideshow();
  }

  useEffect(() => {
    (async () => {
      await fetchData();
    })();
  }, [])

  useEffect(() => {
    if(channel && overlay) 
      fetchOverlayData();
  },[channel?._id, overlay?._id])

  useEffect(() => {
    if(overlaySettings)
      setEditedOverlaySettings(_.cloneDeep(overlaySettings));
  },[overlaySettings])

  useEffect(() => {
    if(sceneSettings)
      setEditedSceneSettings(_.cloneDeep(sceneSettings));
  },[sceneSettings])

  const onChannelChange = channelId => {
    setSelectedIndex(null);
    setPlayingIndex(null);
    setBreakIndex(null);
    setPlaying(false);
    dispatch(RouteState.actions.navigate(Urls.SLIDESHOW_EDIT_ROUTE, { channelId }));
  }
  const onOverlayChange = overlayId => {
    setSelectedIndex(null);
    setPlayingIndex(null);
    setBreakIndex(null);
    setPlaying(false);
    dispatch(RouteState.actions.navigate(Urls.SLIDESHOW_EDIT_OVERLAY_ROUTE, { channelId, overlayId }));
  }

  const showPreview = idx => {
    setPlaying(false);
    setPlayingIndex(null);
    setBreakIndex(null);
    slideshowRef.current.stop();
    slideshowRef.current.show(idx);
  }

  const playSlideshow = idx => {
    idx = idx ?? playingIndex ?? 0;
    setPlaying(true);
    setPlayingIndex(idx);
    setSelectedIndex(null);
    slideshowRef.current.play(idx);
  }

  const stopSlideshow = () => {
    setPlaying(false);
    setBreakIndex(null);
    slideshowRef.current.stop();
  }

  const togglePreviewPlay = idx => {
    playing ?
      stopSlideshow() :
      playSlideshow(idx);
  }

  const onSlideChanged = index => {
    setPlayingIndex(index);
    setBreakIndex(null);
  }

  const onBreak = (index, scene) => {
    setPlayingIndex(null);
    setBreakIndex(index);
  } 

  const onSceneSelect = (sceneId, scene) => {
    let idx = _.findIndex(scenes, { _id: sceneId });

    if(idx === selectedIndex) {
      setSelectedIndex(null);
      playSlideshow(selectedIndex);
    } else {
      setSelectedIndex(idx);
      showPreview(idx);
    }
  }

  const onSaveSlideSettings = () => {
    setSelectedIndex(null);
    setHasChanges(true);
    return playSlideshow(selectedIndex);
  }

  const onOverlaySettingsChange = settings => {
    let newSceneSettings = editedSceneSettings 
      |> _.mapValues(#, s => _.set(s, 'settings', s.settings
        |> _.omitBy(#, (v, k) => v === editedOverlaySettings[k])
        |> _.defaults(#, _.omit(settings, ['pause']))));
    setHasEditedOverlaySettings(true);
    setEditedOverlaySettings(settings);
    setEditedSceneSettings(newSceneSettings);
    setHasChanges(true); 
  }

  const onSceneSettingsChange = (sceneId, settings) => {
    let scene = editedSceneSettings[sceneId];
    scene.settings = _.merge(scene.settings, settings);
    setEditedSceneIds(_.uniq([...editedSceneIds, sceneId]));
    setEditedSceneSettings({...editedSceneSettings, [sceneId]: scene });
    setHasChanges(true);
  }

  const onSaveClick = async e => {
    // Update overlay settings
    if(hasEditedOverlaySettings) {
      await dispatch(Overlay.actions.updateSettings(overlay._id, editedOverlaySettings));
      setHasChanges(false);
    }

    if(!_.isEmpty(editedSceneIds)) {
      // Diff the edited settings by the overlay settings
      let newSettings = editedSceneSettings
        |> _.pick(#, editedSceneIds)
        |> diffSceneSettings(#, overlaySettings)
        |> _.keyBy(#, 'sceneId');
        
      // Update the settings on the campaign model for each edited scene
      let campaignIds = campaignsByScene
        |> _.pick(#, editedSceneIds)
        |> _.values
        |> _.uniq;
    
      for(const campaignId of campaignIds) {
        let sceneIds = scenesByCampaign[campaignId];
        let settings = newSettings 
          |> _.pick(#, sceneIds) 
          |> _.values;
        await dispatch(Campaign.actions.updateSceneSettings(campaignId, settings));
      }
      setHasChanges(false);
    }
  }

  const onBackClick = () => {
    dispatch(RouteState.actions.navigate(navBack));
  }

  return (
    <Content className="slideshow-edit">
      <Scrollbars>
        <div className="container">
          <div className="edit-header">
            <div className="slideshow-title">
              { navBack && <BackIconButton size="xl" onClick={onBackClick} /> }
              Slideshow Editor</div>
            <div className="slideshow-preview">
              <strong>Preview as:</strong>
              <ChannelSelect 
                channels={channels} 
                size="m" 
                selected={channel?._id} 
                onChange={onChannelChange} />
              { hasMultipleOverlays && 
              <OverlaySelect 
                overlays={overlays} 
                size="m" 
                selected={overlay?._id} 
                onChange={onOverlayChange} />}
            </div>
          </div>

          <div className="preview" onClick={e => togglePreviewPlay()}>
          { overlay && 
            <Slideshow
              ref={slideshowRef}
              key={overlay?._id}
              scenes={scenes}
              overlaySettings={editedOverlaySettings}
              sceneSettings={editedSceneSettings}
              onSlideChanged={onSlideChanged}
              onBreak={onBreak} />}
            {!playing && <PlayIconButton onClick={e => togglePreviewPlay()} />}
            {playing && <PauseIconButton onClick={e => togglePreviewPlay()} />}
          </div>

          {!_.isEmpty(scenes) &&
            <SceneStrip
              scenes={scenes}
              overlaySettings={editedOverlaySettings}
              sceneSettings={editedSceneSettings}
              playing={playing}
              playingIndex={playingIndex}
              breakIndex={breakIndex}
              selectedIndex={selectedIndex}
              showAddScene={false}
              onSceneSelect={onSceneSelect} />}

          {_.isEmpty(scenes) &&
            <div className="empty">
              No campaigns are serving slides. Check your campaign targeting settings.
          </div>}
        </div>
      </Scrollbars>
      
      <ActionBar type="fixed">
        <SaveButton onClick={onSaveClick} disabled={!hasChanges} />
      </ActionBar>

      <SidePanel>
        <SidePanelHeader>
          <SidePanelTitle><SettingsIcon /> General Settings</SidePanelTitle>
        </SidePanelHeader>
        <SidePanelBody>
          <OverlaySettings
            showGeneral={true}
            overlaySettings={editedOverlaySettings}
            sceneSettings={editedSceneSettings}
            selectedScene={selectedScene}
            errors={{}}
            onOverlaySettingsChange={onOverlaySettingsChange}
            onSceneSettingsChange={onSceneSettingsChange}
            onSlideSave={onSaveSlideSettings} />
        </SidePanelBody>
        <SidePanelPopup 
          title={<><SettingsIcon /> Slide Settings</>}
          show={showSlidePanel}
          onClose={() => onSceneSelect(selectedScene._id)}>
          { showSlidePanel && 
            <SceneSettings
              scene={selectedScene}
              sceneSettings={editedSceneSettings?.[selectedScene._id]?.settings}
              baseSettings={editedOverlaySettings}
              campaign={_.find(campaigns, { _id: campaignsByScene[selectedScene._id] })}
              onSettingsChange={onSceneSettingsChange}
              onSave={onSaveSlideSettings} />}
        </SidePanelPopup>
      </SidePanel>
    </Content>);
  
}

export default SlideshowEditScreen



